/**
 * Bluesky profile links (`app.bsky.actor.link`) merged with Mackinac linked
 * accounts.
 *
 * Verified 2026-10-27 against:
 * - bluesky-social/atproto `packages/bsky/lexicons/app/bsky/actor/{link,profile,defs}.json`
 * - bluesky-social/atproto `packages/bsky/src/views/profile-link-rules.ts` (49 shortener domains)
 * - bluesky-social/social-app `src/features/profileLinks/` (client list is that set plus
 *   aceimg.in, onlylinks.cc, slicedrve.in, vdey.in, videy.at, videy.lt, videy.my)
 *
 * Writes use the official client's stricter shortener list and the safe order
 * from `useWriteProfileLinks`: create/put link records, then put the profile
 * `links` strongRefs (swapRecord, one retry), then delete removed records.
 * A failed delete does not fail the save.
 *
 * Mackinac OAuth clients request `atproto transition:generic`. In
 * `ScopePermissionsTransition.allowsRepo`, that scope allows every repo
 * operation, including `app.bsky.actor.link`, so the scope string does not
 * change. `ProfileLinkScopeError` is still raised if a PDS returns
 * InsufficientScope (for example a session minted under a future explicit scope).
 */

export const BSKY_LINK_COLLECTION = 'app.bsky.actor.link'
export const BSKY_PROFILE_COLLECTION = 'app.bsky.actor.profile'
export const MACKINAC_PROFILE_COLLECTION = 'social.mackinac.actor.profile'

export const MAX_PROFILE_LINKS = 10
/** Profile link chips shown before the rest collapse behind a "+N more" control. */
export const PROFILE_LINKS_INLINE_LIMIT = 4

/**
 * Splits a profile's links for the chip row: the first `PROFILE_LINKS_INLINE_LIMIT`
 * while collapsed (with how many are hidden, N in "+N more"), or all of them once
 * expanded. Order is preserved and the input is not modified.
 */
export function splitInlineProfileLinks<T>(
    links: readonly T[],
    expanded: boolean
): { visible: T[]; hiddenCount: number } {
    if (expanded || links.length <= PROFILE_LINKS_INLINE_LIMIT) {
        return { visible: [...links], hiddenCount: 0 }
    }
    return {
        visible: links.slice(0, PROFILE_LINKS_INLINE_LIMIT),
        hiddenCount: links.length - PROFILE_LINKS_INLINE_LIMIT,
    }
}
/** Lexicon `maxGraphemes` on `app.bsky.actor.link` `title`. */
export const MAX_TITLE_GRAPHEMES = 40
/** Lexicon `maxLength` (UTF-8 bytes) on the title. */
export const MAX_TITLE_BYTES = 320
/** social-app cuts pill text to 30 graphemes (`MAX_TITLE_LENGTH`). */
export const DISPLAY_TITLE_GRAPHEMES = 30
export const LINK_ICON_MAX_BYTES = 100_000
export const LINK_FAVICON_URL = 'https://cardyb.bsky.app/v1/favicon?url='

export const EXTERNAL_LINK_REL = 'noopener noreferrer nofollow ugc'

/**
 * social-app `SHORTENER_DOMAINS` (a superset of the AppView list). A brand's
 * own short links (youtu.be, amzn.to) are intentionally absent.
 */
export const SHORTENER_DOMAINS = [
    'aceimg.in',
    'acel.me',
    'adf.ly',
    'bit.ly',
    'bitly.com',
    'bl.ink',
    'buff.ly',
    'clck.ru',
    'cutt.ly',
    'fc-lc.xyz',
    'goo.gl',
    'goto.now',
    'is.gd',
    'link2.fm',
    'ln.run',
    'lnk.ua',
    'lnkd.in',
    'n9.cl',
    'onlylinks.cc',
    'ow.ly',
    'psee.io',
    'rb.gy',
    'rebrand.ly',
    'rg.by',
    's.id',
    'short.do',
    'short.gy',
    'shorten.as',
    'shorten.ee',
    'shorten.is',
    'shorten.so',
    'shorten.tv',
    'shorten.world',
    'shorter.me',
    'shortn.icu',
    'shorturl.at',
    'shorturl.lt',
    'shorturl.ly',
    'slicedrve.in',
    'slnt.to',
    'smarturl.lt',
    'smarturl.my',
    'sprl.me',
    'sw.run',
    't.co',
    't.ly',
    'tiny.cc',
    'tinyurl.com',
    'urlr.me',
    'v.gd',
    'vdey.in',
    'videy.at',
    'videy.lt',
    'videy.my',
    'x.gd',
    'zig.ht',
] as const

/**
 * Label values that would blur or filter a link. A pill cannot show a
 * warning, so social-app hides those links (`filterModeratedLinkViews`).
 * Mackinac has no per-label adult opt-in for profile links, so the default
 * blurred set is hidden rather than shown. `!no-unauthenticated` and other
 * informational labels are not in this set.
 */
const HIDING_LABELS = new Set([
    'porn',
    'sexual',
    'nudity',
    'nsfl',
    'gore',
    'graphic-media',
    '!hide',
    '!warn',
])

export type ProfileLinkSource = 'bsky' | 'mackinac' | 'mastodon'

export interface ProfileLink {
    source: ProfileLinkSource
    url: string
    /** Stored title. Empty when the record has none; chips fall back to the host. */
    title: string
    /** Mackinac platform id (`github`, `link`, …). Preferred over the favicon when specific. */
    platformIcon?: string
    /** AppView CDN URL, or one built from the link record's icon blob. */
    iconUrl?: string
    uri?: string
    cid?: string
    verifiedAt?: string
}

export interface DesiredBlueskyLink {
    url: string
    title?: string
    uri?: string
}

export type ProfileLinkSkipReason = 'insecure' | 'shortener' | 'invalid' | 'duplicate' | 'cap' | 'title'

export interface SkippedProfileLink {
    url: string
    name: string
    reason: ProfileLinkSkipReason
}

export interface ProfileLinkRepo {
    getRecord(args: {
        repo: string
        collection: string
        rkey: string
    }): Promise<{ cid?: string; value: unknown } | null>
    putRecord(args: {
        repo: string
        collection: string
        rkey: string
        record: Record<string, unknown>
        swapRecord?: string | null
    }): Promise<{ uri: string; cid: string }>
    createRecord(args: {
        repo: string
        collection: string
        record: Record<string, unknown>
    }): Promise<{ uri: string; cid: string }>
    deleteRecord(args: { repo: string; collection: string; rkey: string }): Promise<void>
    uploadBlob?(bytes: Uint8Array, mimeType: string): Promise<unknown>
}

export class ProfileLinkScopeError extends Error {
    readonly code = 'insufficient_scope' as const
    constructor() {
        super('InsufficientScope')
        this.name = 'ProfileLinkScopeError'
    }
}

export class ProfileLinkValidationError extends Error {
    readonly reason: ProfileLinkSkipReason
    readonly url: string
    constructor(reason: ProfileLinkSkipReason, url: string) {
        super(reason)
        this.name = 'ProfileLinkValidationError'
        this.reason = reason
        this.url = url
    }
}

export function isRawScopeError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false
    const record = err as { error?: unknown; message?: unknown }
    if (record.error === 'InsufficientScope') return true
    return typeof record.message === 'string' && record.message.includes('InsufficientScope')
}

export function isSwapConflict(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false
    const record = err as { status?: unknown; error?: unknown }
    return record.status === 409 || record.error === 'InvalidSwap'
}

export function isRecordNotFound(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false
    const record = err as { status?: unknown; error?: unknown }
    return record.error === 'RecordNotFound' || record.status === 404
}

function rethrowKnown(err: unknown): never {
    if (err instanceof ProfileLinkScopeError || err instanceof ProfileLinkValidationError) throw err
    if (isRawScopeError(err)) throw new ProfileLinkScopeError()
    throw err
}

export function countGraphemes(value: string): number {
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        let count = 0
        for (const _ of segmenter.segment(value)) count += 1
        return count
    }
    return Array.from(value).length
}

export function truncateGraphemes(value: string, max: number): string {
    if (countGraphemes(value) <= max) return value
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        let out = ''
        let count = 0
        for (const part of segmenter.segment(value)) {
            if (count >= max) break
            out += part.segment
            count += 1
        }
        return out.trim()
    }
    return Array.from(value).slice(0, max).join('').trim()
}

function utf8Bytes(value: string): number {
    return new TextEncoder().encode(value).length
}

/**
 * De-dupe key: lowercase scheme and host, strip a leading `www.`, strip one
 * trailing slash from the path, keep the path and query, drop the hash.
 */
export function normalizeProfileLinkKey(url: string): string | null {
    let parsed: URL
    try {
        parsed = new URL(url.trim())
    } catch {
        return null
    }
    const protocol = parsed.protocol.toLowerCase()
    if (protocol !== 'http:' && protocol !== 'https:') return null
    if (parsed.username || parsed.password) return null
    let host = parsed.hostname.toLowerCase()
    if (host.startsWith('www.')) host = host.slice(4)
    let path = parsed.pathname
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1)
    if (path === '/') path = ''
    return `${protocol}//${host}${path}${parsed.search}`
}

function hostMatchesDomain(host: string, domain: string): boolean {
    return host === domain || host.endsWith(`.${domain}`)
}

export function isShortenerUrl(url: string): boolean {
    let parsed: URL
    try {
        parsed = new URL(url.trim())
    } catch {
        return false
    }
    const host = parsed.hostname.toLowerCase()
    return SHORTENER_DOMAINS.some(domain => hostMatchesDomain(host, domain))
}

/** https, no credentials, not a shortener. */
export function isAllowedBlueskyProfileLinkUrl(url: string): boolean {
    let parsed: URL
    try {
        parsed = new URL(url.trim())
    } catch {
        return false
    }
    if (parsed.protocol !== 'https:') return false
    if (parsed.username || parsed.password) return false
    return !isShortenerUrl(url)
}

export type BlueskyUrlIssue = 'invalid' | 'insecure' | 'shortener'

export function blueskyUrlIssue(url: string): BlueskyUrlIssue | null {
    const trimmed = url.trim()
    if (!trimmed) return 'invalid'
    let parsed: URL
    try {
        parsed = new URL(trimmed)
    } catch {
        return 'invalid'
    }
    if (parsed.username || parsed.password) return 'invalid'
    if (parsed.protocol === 'http:') return 'insecure'
    if (parsed.protocol !== 'https:') return 'invalid'
    if (isShortenerUrl(trimmed)) return 'shortener'
    return null
}

export function clampBlueskyTitle(title: string): string | undefined {
    let next = title.trim()
    if (!next) return undefined
    next = truncateGraphemes(next, MAX_TITLE_GRAPHEMES)
    while (next && utf8Bytes(next) > MAX_TITLE_BYTES) {
        next = truncateGraphemes(next, countGraphemes(next) - 1)
    }
    return next || undefined
}

export function blueskyTitleIssue(title: string | undefined): 'title' | null {
    if (!title || !title.trim()) return null
    const trimmed = title.trim()
    if (countGraphemes(trimmed) > MAX_TITLE_GRAPHEMES) return 'title'
    if (utf8Bytes(trimmed) > MAX_TITLE_BYTES) return 'title'
    return null
}

export function didFromAtUri(uri: string): string | undefined {
    if (!uri.startsWith('at://')) return undefined
    const rest = uri.slice('at://'.length)
    const slash = rest.indexOf('/')
    const did = slash === -1 ? rest : rest.slice(0, slash)
    return did.startsWith('did:') ? did : undefined
}

export function rkeyFromAtUri(uri: string): string | undefined {
    const match = /^at:\/\/[^/]+\/[^/]+\/(.+)$/.exec(uri)
    return match?.[1]
}

export function hostnameLabel(url: string): string {
    try {
        return new URL(url).hostname.replace(/^www\./i, '')
    } catch {
        return url
    }
}

export function chipLabel(
    source: ProfileLinkSource | undefined,
    title: string,
    url: string
): string {
    const raw = title.trim() || hostnameLabel(url)
    if (source === 'bsky') return truncateGraphemes(raw, DISPLAY_TITLE_GRAPHEMES)
    return raw
}

export function isSpecificPlatformIcon(icon: string | undefined): boolean {
    return !!icon && icon !== 'link'
}

export function isBlueskyCdnIconUrl(url: string | undefined): boolean {
    if (!url) return false
    try {
        const parsed = new URL(url)
        return parsed.protocol === 'https:' && parsed.hostname === 'cdn.bsky.app'
    } catch {
        return false
    }
}

export function profileLinkIconCdnUrl(did: string, blobCid: string): string {
    return `https://cdn.bsky.app/img/avatar/plain/${encodeURIComponent(did)}/${encodeURIComponent(blobCid)}`
}

export function linkLabelsHide(labels: { val?: string; neg?: boolean }[] | undefined): boolean {
    if (!labels?.length) return false
    return labels.some(label => {
        if (!label.val || label.neg) return false
        if (HIDING_LABELS.has(label.val)) return true
        return label.val.startsWith('!hide') || label.val.startsWith('!warn')
    })
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function blobCidOf(icon: unknown): string | undefined {
    if (!isPlainObject(icon)) return undefined
    const ref = icon.ref
    if (typeof ref === 'string') return ref
    if (isPlainObject(ref) && typeof ref.$link === 'string') return ref.$link
    return undefined
}

function labelsFromUnknown(value: unknown): { val?: string; neg?: boolean }[] | undefined {
    if (!Array.isArray(value)) return undefined
    return value.filter(isPlainObject).map(label => ({
        val: typeof label.val === 'string' ? label.val : undefined,
        neg: label.neg === true,
    }))
}

/** Self-labels on a record (`com.atproto.label.defs#selfLabels`). */
function selfLabels(value: Record<string, unknown>): { val?: string }[] | undefined {
    const labels = value.labels
    if (!isPlainObject(labels) || !Array.isArray(labels.values)) return undefined
    return labels.values.filter(isPlainObject).map(label => ({
        val: typeof label.val === 'string' ? label.val : undefined,
    }))
}

export interface MackinacLinkInput {
    name: string
    url: string
    platformIcon?: string
    verifiedAt?: string
}

/**
 * Bluesky links first (their array order), then Mackinac-only links.
 * Same normalized URL collapses to one row: Bluesky title wins when it is
 * non-empty, and the Mackinac platform icon is kept.
 */
export function mergeProfileLinks(input: {
    bluesky?: ProfileLink[]
    mackinac?: MackinacLinkInput[]
    mastodon?: MackinacLinkInput[]
}): ProfileLink[] {
    const seen = new Set<string>()
    const merged: ProfileLink[] = []

    for (const link of input.bluesky ?? []) {
        const key = normalizeProfileLinkKey(link.url)
        if (!key || seen.has(key)) continue
        if (!isAllowedBlueskyProfileLinkUrl(link.url)) continue
        seen.add(key)
        merged.push({ ...link, source: 'bsky' })
    }

    const mackinacByKey = new Map<string, MackinacLinkInput>()
    for (const link of input.mackinac ?? []) {
        const key = normalizeProfileLinkKey(link.url)
        if (!key || mackinacByKey.has(key)) continue
        mackinacByKey.set(key, link)
    }

    for (const link of merged) {
        const key = normalizeProfileLinkKey(link.url)
        if (!key) continue
        const mackinac = mackinacByKey.get(key)
        if (!mackinac) continue
        if (isSpecificPlatformIcon(mackinac.platformIcon)) {
            link.platformIcon = mackinac.platformIcon
        }
        if (!link.title.trim() && mackinac.name.trim()) {
            link.title = mackinac.name.trim()
        }
    }

    for (const link of input.mackinac ?? []) {
        const key = normalizeProfileLinkKey(link.url)
        if (!key || seen.has(key)) continue
        if (blueskyUrlIssue(link.url) === 'invalid' && !isHttpUrl(link.url)) continue
        // Mackinac display still allows http(s). De-dupe only. Don't drop http here;
        // callers that only want clickable links already filtered. Keep http so the
        // existing Mackinac chip behavior is unchanged for Mackinac-only rows.
        if (!isHttpUrl(link.url) && !isAllowedBlueskyProfileLinkUrl(link.url)) continue
        seen.add(key)
        merged.push({
            source: 'mackinac',
            url: link.url,
            title: link.name,
            platformIcon: link.platformIcon,
            verifiedAt: link.verifiedAt,
        })
    }

    for (const link of input.mastodon ?? []) {
        const key = normalizeProfileLinkKey(link.url)
        if (!key || seen.has(key)) continue
        if (!isHttpUrl(link.url)) continue
        seen.add(key)
        merged.push({
            source: 'mastodon',
            url: link.url,
            title: link.name,
            platformIcon: link.platformIcon,
            verifiedAt: link.verifiedAt,
        })
    }

    return merged
}

function isHttpUrl(url: string): boolean {
    try {
        const protocol = new URL(url.trim()).protocol
        return protocol === 'http:' || protocol === 'https:'
    } catch {
        return false
    }
}

/**
 * AppView `profileLinkView` list. Returns `undefined` when `links` is missing
 * so the caller can fall back to strongRefs. An empty array means the field
 * was present and nothing survived.
 */
export function blueskyLinksFromViews(links: unknown, did: string): ProfileLink[] | undefined {
    if (links === undefined) return undefined
    if (!Array.isArray(links)) return []
    const out: ProfileLink[] = []
    const seen = new Set<string>()
    for (const entry of links) {
        if (out.length >= MAX_PROFILE_LINKS) break
        if (!isPlainObject(entry)) continue
        const uri = typeof entry.uri === 'string' ? entry.uri : undefined
        const url = typeof entry.url === 'string' ? entry.url : undefined
        if (!uri || !url) continue
        if (didFromAtUri(uri) !== did) continue
        if (!isAllowedBlueskyProfileLinkUrl(url)) continue
        if (linkLabelsHide(labelsFromUnknown(entry.labels))) continue
        const key = normalizeProfileLinkKey(url)
        if (!key || seen.has(key)) continue
        seen.add(key)
        const title = typeof entry.title === 'string' ? entry.title.trim() : ''
        const icon = typeof entry.icon === 'string' ? entry.icon : undefined
        out.push({
            source: 'bsky',
            url,
            title,
            iconUrl: isBlueskyCdnIconUrl(icon) ? icon : undefined,
            uri,
            cid: typeof entry.cid === 'string' ? entry.cid : undefined,
        })
    }
    return out
}

export interface ResolvedBlueskyLink {
    uri: string
    cid: string
    url: string
    title?: string
    record: Record<string, unknown>
}

/** Profile `links` strongRefs plus the records that still exist. */
export async function resolveBlueskyLinkRecords(
    repo: ProfileLinkRepo,
    did: string,
    profileValue: Record<string, unknown> | null
): Promise<ResolvedBlueskyLink[]> {
    const raw = profileValue?.links
    if (!Array.isArray(raw)) return []
    const out: ResolvedBlueskyLink[] = []
    const seen = new Set<string>()
    for (const ref of raw.slice(0, MAX_PROFILE_LINKS)) {
        if (!isPlainObject(ref) || typeof ref.uri !== 'string') continue
        if (didFromAtUri(ref.uri) !== did) continue
        const rkey = rkeyFromAtUri(ref.uri)
        if (!rkey) continue
        let record: { cid?: string; value: unknown } | null
        try {
            record = await repo.getRecord({ repo: did, collection: BSKY_LINK_COLLECTION, rkey })
        } catch (err) {
            if (isRecordNotFound(err)) continue
            rethrowKnown(err)
        }
        if (!record || !isPlainObject(record.value)) continue
        const url = record.value.url
        if (typeof url !== 'string' || !isAllowedBlueskyProfileLinkUrl(url)) continue
        if (linkLabelsHide(selfLabels(record.value))) continue
        const key = normalizeProfileLinkKey(url)
        if (!key || seen.has(key)) continue
        seen.add(key)
        const title = typeof record.value.title === 'string' ? record.value.title.trim() : ''
        const cid = record.cid ?? (typeof ref.cid === 'string' ? ref.cid : '')
        if (!cid) continue
        out.push({
            uri: ref.uri,
            cid,
            url,
            title: title || undefined,
            record: record.value,
        })
    }
    return out
}

export function resolvedToProfileLinks(links: ResolvedBlueskyLink[], did: string): ProfileLink[] {
    return links.map(link => {
        const iconCid = blobCidOf(link.record.icon)
        return {
            source: 'bsky' as const,
            url: link.url,
            title: link.title ?? '',
            iconUrl: iconCid ? profileLinkIconCdnUrl(did, iconCid) : undefined,
            uri: link.uri,
            cid: link.cid,
        }
    })
}

export function validateDesiredBlueskyLinks(links: DesiredBlueskyLink[]): void {
    if (links.length > MAX_PROFILE_LINKS) {
        throw new ProfileLinkValidationError('cap', links[MAX_PROFILE_LINKS]?.url ?? '')
    }
    const seen = new Set<string>()
    for (const link of links) {
        const issue = blueskyUrlIssue(link.url)
        if (issue) throw new ProfileLinkValidationError(issue, link.url.trim())
        if (blueskyTitleIssue(link.title)) throw new ProfileLinkValidationError('title', link.url.trim())
        const key = normalizeProfileLinkKey(link.url)
        if (!key || seen.has(key)) throw new ProfileLinkValidationError('duplicate', link.url.trim())
        seen.add(key)
    }
}

export interface PublishPlan {
    toAdd: { url: string; title?: string; name: string }[]
    skipped: SkippedProfileLink[]
    /** Mackinac links whose URL is already on Bluesky. Not an error. */
    alreadyOnBluesky: SkippedProfileLink[]
}

/**
 * Converts Mackinac links into Bluesky creates. Does not mutate the Mackinac
 * record. Idempotent: a second call with the same server state adds nothing.
 */
export function planPublishMackinacLinks(
    mackinac: { name: string; url: string }[],
    existingBluesky: { url: string }[]
): PublishPlan {
    const seen = new Set<string>()
    for (const link of existingBluesky) {
        const key = normalizeProfileLinkKey(link.url)
        if (key) seen.add(key)
    }
    let slots = Math.max(0, MAX_PROFILE_LINKS - existingBluesky.length)
    const toAdd: PublishPlan['toAdd'] = []
    const skipped: SkippedProfileLink[] = []
    const alreadyOnBluesky: SkippedProfileLink[] = []
    for (const link of mackinac) {
        const url = link.url.trim()
        const name = link.name.trim()
        const issue = blueskyUrlIssue(url)
        if (issue) {
            skipped.push({ url, name, reason: issue })
            continue
        }
        const key = normalizeProfileLinkKey(url)
        if (!key || seen.has(key)) {
            alreadyOnBluesky.push({ url, name, reason: 'duplicate' })
            continue
        }
        if (slots <= 0) {
            skipped.push({ url, name, reason: 'cap' })
            continue
        }
        seen.add(key)
        slots -= 1
        toAdd.push({ url, title: clampBlueskyTitle(name), name })
    }
    return { toAdd, skipped, alreadyOnBluesky }
}

interface LinkWriteOp {
    kind: 'keep' | 'put' | 'create'
    url: string
    title?: string
    uri?: string
    rkey?: string
    record?: Record<string, unknown>
    /** True when the destination URL changed and a new favicon should be tried. */
    refreshIcon: boolean
}

function planLinkWrites(existing: ResolvedBlueskyLink[], desired: DesiredBlueskyLink[]): {
    ops: LinkWriteOp[]
    removed: { uri: string; rkey: string }[]
} {
    const existingByUri = new Map(existing.map(link => [link.uri, link]))
    const existingByKey = new Map<string, ResolvedBlueskyLink>()
    for (const link of existing) {
        const key = normalizeProfileLinkKey(link.url)
        if (key && !existingByKey.has(key)) existingByKey.set(key, link)
    }
    const used = new Set<string>()
    const ops: LinkWriteOp[] = []
    for (const link of desired) {
        const title = link.title?.trim() ? link.title.trim() : undefined
        const byUri = link.uri ? existingByUri.get(link.uri) : undefined
        const key = normalizeProfileLinkKey(link.url)
        const match = byUri ?? (key ? existingByKey.get(key) : undefined)
        if (match && !used.has(match.uri)) {
            used.add(match.uri)
            const unchanged = match.url === link.url.trim() && (match.title ?? '') === (title ?? '')
            if (unchanged) {
                ops.push({
                    kind: 'keep',
                    url: match.url,
                    title: match.title,
                    uri: match.uri,
                    refreshIcon: false,
                })
            } else {
                ops.push({
                    kind: 'put',
                    url: link.url.trim(),
                    title,
                    uri: match.uri,
                    rkey: rkeyFromAtUri(match.uri),
                    record: match.record,
                    refreshIcon: match.url !== link.url.trim(),
                })
            }
        } else {
            ops.push({
                kind: 'create',
                url: link.url.trim(),
                title,
                refreshIcon: true,
            })
        }
    }
    const removed = existing
        .filter(link => !used.has(link.uri))
        .flatMap(link => {
            const rkey = rkeyFromAtUri(link.uri)
            return rkey ? [{ uri: link.uri, rkey }] : []
        })
    return { ops, removed }
}

async function loadIcon(
    repo: ProfileLinkRepo,
    url: string,
    favicon: ((url: string) => Promise<{ bytes: Uint8Array; mimeType: string } | undefined>) | undefined
): Promise<unknown> {
    if (!repo.uploadBlob || !favicon) return undefined
    try {
        const icon = await favicon(url)
        if (!icon) return undefined
        if (icon.bytes.byteLength === 0 || icon.bytes.byteLength > LINK_ICON_MAX_BYTES) return undefined
        if (icon.mimeType !== 'image/png' && icon.mimeType !== 'image/jpeg' && icon.mimeType !== 'image/webp') {
            return undefined
        }
        return await repo.uploadBlob(icon.bytes, icon.mimeType)
    } catch (err) {
        if (isRawScopeError(err)) throw new ProfileLinkScopeError()
        return undefined
    }
}

async function readProfile(
    repo: ProfileLinkRepo,
    did: string
): Promise<{ cid?: string; value: Record<string, unknown> } | null> {
    try {
        const record = await repo.getRecord({
            repo: did,
            collection: BSKY_PROFILE_COLLECTION,
            rkey: 'self',
        })
        if (!record) return null
        if (!isPlainObject(record.value)) {
            throw new Error('app.bsky.actor.profile record is not an object')
        }
        return { cid: record.cid, value: record.value }
    } catch (err) {
        if (isRecordNotFound(err)) return null
        rethrowKnown(err)
    }
}

async function putProfileLinks(
    repo: ProfileLinkRepo,
    did: string,
    refs: { uri: string; cid: string }[]
): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt++) {
        const current = await readProfile(repo, did)
        if (current && !current.cid) {
            throw new Error('app.bsky.actor.profile record is missing a cid')
        }
        const record: Record<string, unknown> = {
            ...(current?.value ?? {}),
            $type: BSKY_PROFILE_COLLECTION,
        }
        if (refs.length > 0) record.links = refs
        else delete record.links
        try {
            await repo.putRecord({
                repo: did,
                collection: BSKY_PROFILE_COLLECTION,
                rkey: 'self',
                record,
                swapRecord: current?.cid ?? null,
            })
            return
        } catch (err) {
            if (attempt === 0 && isSwapConflict(err)) continue
            rethrowKnown(err)
        }
    }
}

export interface SaveBlueskyLinksResult {
    refs: { uri: string; cid: string }[]
    /** Removed link records whose delete failed. The profile no longer points at them. */
    deleteFailures: number
}

/**
 * Writes `desired` as the profile's Bluesky links. `desired` is the full
 * ordered list, not a patch. Unchanged records are left in place.
 */
export async function saveBlueskyProfileLinks(
    repo: ProfileLinkRepo,
    did: string,
    desired: DesiredBlueskyLink[],
    opts?: {
        icons?: boolean
        favicon?: (url: string) => Promise<{ bytes: Uint8Array; mimeType: string } | undefined>
    }
): Promise<SaveBlueskyLinksResult> {
    validateDesiredBlueskyLinks(desired)
    const profile = await readProfile(repo, did)
    const existing = await resolveBlueskyLinkRecords(repo, did, profile?.value ?? null)
    const { ops, removed } = planLinkWrites(existing, desired)
    const favicon = opts?.icons === false ? undefined : (opts?.favicon ?? fetchBlueskyLinkFavicon)
    const refs: { uri: string; cid: string }[] = []
    for (const op of ops) {
        if (op.kind === 'keep' && op.uri) {
            const match = existing.find(link => link.uri === op.uri)
            if (!match) continue
            refs.push({ uri: match.uri, cid: match.cid })
            continue
        }
        let icon: unknown
        if (op.refreshIcon) icon = await loadIcon(repo, op.url, favicon)
        const createdAt = new Date().toISOString()
        if (op.kind === 'put' && op.rkey && op.record) {
            const record: Record<string, unknown> = {
                ...op.record,
                $type: BSKY_LINK_COLLECTION,
                url: op.url,
                createdAt,
            }
            if (op.title) record.title = op.title
            else delete record.title
            if (icon) record.icon = icon
            try {
                const written = await repo.putRecord({
                    repo: did,
                    collection: BSKY_LINK_COLLECTION,
                    rkey: op.rkey,
                    record,
                })
                refs.push({ uri: written.uri || op.uri!, cid: written.cid })
            } catch (err) {
                rethrowKnown(err)
            }
            continue
        }
        const record: Record<string, unknown> = {
            $type: BSKY_LINK_COLLECTION,
            url: op.url,
            createdAt,
        }
        if (op.title) record.title = op.title
        if (icon) record.icon = icon
        try {
            const created = await repo.createRecord({
                repo: did,
                collection: BSKY_LINK_COLLECTION,
                record,
            })
            refs.push({ uri: created.uri, cid: created.cid })
        } catch (err) {
            rethrowKnown(err)
        }
    }

    const previous = existing.map(link => ({ uri: link.uri, cid: link.cid }))
    const same =
        refs.length === previous.length &&
        refs.every((ref, index) => ref.uri === previous[index]?.uri && ref.cid === previous[index]?.cid)
    if (!same || removed.length > 0) {
        await putProfileLinks(repo, did, refs)
    }

    let deleteFailures = 0
    for (const link of removed) {
        try {
            await repo.deleteRecord({ repo: did, collection: BSKY_LINK_COLLECTION, rkey: link.rkey })
        } catch {
            deleteFailures += 1
        }
    }
    return { refs, deleteFailures }
}

export interface PublishMackinacResult {
    added: { url: string; title?: string; name: string }[]
    skipped: SkippedProfileLink[]
    alreadyOnBluesky: SkippedProfileLink[]
}

export async function readMackinacLinkInputs(
    repo: ProfileLinkRepo,
    did: string
): Promise<{ name: string; url: string }[]> {
    let record: { cid?: string; value: unknown } | null
    try {
        record = await repo.getRecord({
            repo: did,
            collection: MACKINAC_PROFILE_COLLECTION,
            rkey: 'self',
        })
    } catch (err) {
        if (isRecordNotFound(err)) return []
        rethrowKnown(err)
    }
    if (!record || !isPlainObject(record.value) || !Array.isArray(record.value.links)) return []
    const links: { name: string; url: string }[] = []
    for (const entry of record.value.links) {
        if (!isPlainObject(entry)) continue
        const url = typeof entry.link === 'string' ? entry.link : ''
        const name = typeof entry.name === 'string' ? entry.name : ''
        if (!url) continue
        links.push({ name, url })
    }
    return links
}

/** Opt-in. Leaves the Mackinac record untouched. */
export async function publishMackinacLinksToBluesky(
    repo: ProfileLinkRepo,
    did: string,
    opts?: {
        icons?: boolean
        favicon?: (url: string) => Promise<{ bytes: Uint8Array; mimeType: string } | undefined>
    }
): Promise<PublishMackinacResult> {
    const mackinac = await readMackinacLinkInputs(repo, did)
    const profile = await readProfile(repo, did)
    const existing = await resolveBlueskyLinkRecords(repo, did, profile?.value ?? null)
    const plan = planPublishMackinacLinks(mackinac, existing)
    if (plan.toAdd.length === 0) {
        return { added: [], skipped: plan.skipped, alreadyOnBluesky: plan.alreadyOnBluesky }
    }
    const desired: DesiredBlueskyLink[] = [
        ...existing.map(link => ({ url: link.url, title: link.title, uri: link.uri })),
        ...plan.toAdd.map(link => ({ url: link.url, title: link.title })),
    ]
    await saveBlueskyProfileLinks(repo, did, desired, opts)
    return { added: plan.toAdd, skipped: plan.skipped, alreadyOnBluesky: plan.alreadyOnBluesky }
}

export interface BlueskyProfilePatch {
    displayName: string
    description: string
    pronouns?: string
    avatar?: unknown
    removeAvatar?: boolean
}

/**
 * getRecord + putRecord on `app.bsky.actor.profile`, spreading the stored
 * record verbatim. `agent.upsertProfile` hands the updater `undefined` when
 * the stored record fails the lexicon in the installed SDK, then still
 * swapRecords over the real record — which drops `links` and labels.
 */
export async function patchBlueskyActorProfile(
    repo: ProfileLinkRepo,
    did: string,
    patch: BlueskyProfilePatch
): Promise<void> {
    const apply = (base: Record<string, unknown>): Record<string, unknown> => {
        const next: Record<string, unknown> = {
            ...base,
            $type: BSKY_PROFILE_COLLECTION,
            displayName: patch.displayName,
            description: patch.description,
        }
        if (patch.pronouns) next.pronouns = patch.pronouns
        else delete next.pronouns
        if (patch.avatar !== undefined) next.avatar = patch.avatar
        else if (patch.removeAvatar) delete next.avatar
        return next
    }

    for (let attempt = 0; attempt < 2; attempt++) {
        const current = await readProfile(repo, did)
        if (current && !current.cid) {
            throw new Error('app.bsky.actor.profile record is missing a cid')
        }
        try {
            await repo.putRecord({
                repo: did,
                collection: BSKY_PROFILE_COLLECTION,
                rkey: 'self',
                record: apply(current?.value ?? {}),
                swapRecord: current?.cid ?? null,
            })
            return
        } catch (err) {
            if (attempt === 0 && isSwapConflict(err)) continue
            rethrowKnown(err)
        }
    }
}

const FAVICON_MIMES = new Set(['image/png', 'image/jpeg', 'image/webp'])

/** cardyb favicon. Failures resolve to undefined so a missing icon never blocks a save. */
export async function fetchBlueskyLinkFavicon(
    url: string
): Promise<{ bytes: Uint8Array; mimeType: string } | undefined> {
    try {
        const response = await fetch(`${LINK_FAVICON_URL}${encodeURIComponent(url)}`, {
            signal: AbortSignal.timeout(8000),
        })
        if (!response.ok) return undefined
        const mime = (response.headers.get('content-type') ?? '').split(';')[0]?.trim().toLowerCase()
        if (!mime || !FAVICON_MIMES.has(mime)) return undefined
        const bytes = new Uint8Array(await response.arrayBuffer())
        if (bytes.byteLength === 0 || bytes.byteLength > LINK_ICON_MAX_BYTES) return undefined
        return { bytes, mimeType: mime }
    } catch {
        return undefined
    }
}
